import { ColumnsType } from 'antd/es/table';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import classNames from 'classnames';
import {
  ContractItemPriceRaise,
  ContractItemPriceRaiseStatus,
  PriceRaiseTab,
  getPriceRaiseCustomerName,
  getNewPriceInvoicePeriod,
  getPriceRaisePercent,
  priceRaiseStatusBadgeColorMap,
  priceRaiseStatusLabelMap,
  useGetContractItemPriceRaisesQuery,
} from 'entities/Contract';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { UserPermissions } from 'entities/User';
import { SendPriceRaiseNotification, useSendPriceRaiseNotification } from 'features/SendPriceRaiseNotification';
import { useTableFilterContext } from 'features/TableFilter';
import { FC, memo, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { getClientDateFormat, getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { transformPriceRaiseListFilters } from '../utils/transformPriceRaiseListFilters';
import { PriceRaiseListFilter } from './PriceRaiseListFilter';

interface PriceRaiseProcessedTableProps {
  tab: PriceRaiseTab;
}

const processedStatuses = `${ContractItemPriceRaiseStatus.ACCEPTED},${ContractItemPriceRaiseStatus.REJECTED}`;

export const PriceRaiseProcessedTable: FC<PriceRaiseProcessedTableProps> = memo((props) => {
  const { tab } = props;

  const { t } = useAppTranslation(['contracts', 'common']);
  const isForbiddenManagePriceRaise = useCheckPermission(UserPermissions.MANAGE_PRICE_RAISE);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currencySymbol = useGetCurrencySymbol();

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();
  const {
    open: openSendNotification,
    close: closeSendNotification,
    send: sendNotification,
    isOpened: isSendNotificationOpened,
    isLoading: isSendNotificationLoading,
  } = useSendPriceRaiseNotification();

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const goToUserContracts = useCallback(
    (userId: string): void => {
      if (userId) {
        navigate(`${AppRoutes.USERS}/${userId}/contracts`);
      }
    },
    [navigate],
  );

  const tableActions: Array<ServerTableActions<ContractItemPriceRaise>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t],
  );

  const rowActions: Array<ServerTableRowActions<ContractItemPriceRaise>> = useMemo(() => {
    if (tab !== PriceRaiseTab.PROCESSED) {
      return [];
    }

    return [
      {
        name: 'sendNotification',
        icon: <EmailIcon className="[&>path]:stroke-primary" />,
        theme: 'clear',
        description: t('Send notification'),
        onClick: (item) => {
          openSendNotification(item.contractItemPriceRaiseId);
        },
        isDisabled: () => isForbiddenManagePriceRaise,
        isHidden: (item) => item.status !== ContractItemPriceRaiseStatus.ACCEPTED,
      },
    ];
  }, [isForbiddenManagePriceRaise, openSendNotification, t, tab]);

  const columns = useMemo<ColumnsType<ContractItemPriceRaise>>(
    () => [
      {
        title: <div className="text-sm">{t('Warehouse')}</div>,
        key: 'warehouse',
        render: (_, record) => {
          const { noticeLeaveDate, noticeGivenDate, leaveDate } = record.contract;
          const isUnfinishedTab = tab === PriceRaiseTab.UNFINISHED;
          const badgeLines = [
            noticeLeaveDate && `${t('Planned move-out date')}: ${getClientDateFormat(noticeLeaveDate)}`,
            noticeLeaveDate && `${t('Move-out notice date')}: ${getClientDateFormat(noticeGivenDate)}`,
            isUnfinishedTab && leaveDate && `${t('Contract leave date')}: ${getClientDateFormat(leaveDate)}`,
          ].filter((line): line is string => Boolean(line));
          const showSideBadge = tab === PriceRaiseTab.PROCESSED ? Boolean(noticeLeaveDate) : isUnfinishedTab && badgeLines.length > 0;

          return (
            <div>
              <Link className="text-accent underline font-normal" to={`${AppRoutes.WAREHOUSES}/${record.contract.warehouse.warehouseId}`}>
                {record.contract.warehouse.name}
              </Link>
              <RowBadge color={priceRaiseStatusBadgeColorMap[record.status]} title={t(priceRaiseStatusLabelMap[record.status])} />
              {showSideBadge && (
                <RowSideBadge color="error" toolTipColor="white" toolTipFontColor="primary" title={badgeLines.join('\n')} />
              )}
            </div>
          );
        },
      },
      {
        title: (
          <>
            <div className="text-sm">{t('Contract')}</div>
            <div className="text-sm">{t('Start date')}</div>
          </>
        ),
        key: 'startDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div
            className="text-accent underline font-normal cursor-pointer"
            onClick={() => {
              openContractDetails(record.contract.contractId);
            }}
          >
            <div>#{record.contract.contractNumber}</div>
            <div className="text-sm">{getClientDateFormat(record.contract.startDate)}</div>
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('Customer')}</div>,
        key: 'customer',
        render: (_, record) => (
          <div
            onClick={() => {
              goToUserContracts(record.contract.user.userId);
            }}
            className="flex flex-col"
          >
            <div className="text-xs text-accent cursor-pointer underline">{record.contract.user.email}</div>
            <div className="font-bold text-accent cursor-pointer underline">{getPriceRaiseCustomerName(record.contract)}</div>
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('Size / Unit')}</div>,
        key: 'box',
        render: (_, record) => (
          <>
            <div>
              {record.contractItem?.box?.square} {getSizeCodeTypeString(record.contractItem?.box?.sizeCode?.sizeCodeType)}
            </div>
            <div className="font-normal text-sm">{record.contractItem?.box?.name}</div>
          </>
        ),
      },
      {
        title: <div className="text-sm">{t('Current price')}</div>,
        key: 'contractUnitPrice',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div>
            {record.oldPrice} {currencySymbol}
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('New price')}</div>,
        key: 'proposedPrice',
        render: (_, record) => {
          const percent = getPriceRaisePercent(record.oldPrice, record.newPrice);
          const percentLabel = `${percent > 0 ? '+' : ''}${percent}%`;

          return (
            <div>
              <div>
                {t('New price')}: {record.newPrice} {currencySymbol}
              </div>
              <div className="text-sm font-normal mt-1">({percentLabel})</div>
            </div>
          );
        },
      },
      {
        title: <div className="text-sm">{t('Processed date')}</div>,
        key: 'processedAt',
        render: (_, record) => (record.createdAt ? <span className="text-sm">{getClientDateFormat(record.createdAt)}</span> : null),
      },
      {
        title: <div className="text-sm">{t('Notification sent date')}</div>,
        key: 'notificationSentAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) =>
          record.notificationSentAt ? (
            <span className="text-sm">{getClientDateWithMinuteTimeFormat(record.notificationSentAt)}</span>
          ) : null,
      },
      {
        title: <div className="text-sm">{t('Review again date')}</div>,
        key: 'nextReviewDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) =>
          record.nextReviewDate ? (
            <span className="text-sm text-error font-semibold">{getClientDateFormat(record.nextReviewDate)}</span>
          ) : null,
      },
      {
        title: <div className="text-sm">{t('Price raise date')}</div>,
        key: 'priceRaiseDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => {
          const period = getNewPriceInvoicePeriod(record.priceRaiseDate, record.contract);

          if (!record.priceRaiseDate) {
            return null;
          }

          return (
            <div className="text-xs">
              <div className="text-success font-semibold mb-2">{`${t('Invoicing date')}: ${getClientDateFormat(
                record.priceRaiseDate,
              )}`}</div>
              {period && (
                <div>
                  {`${t('New price invoice period')}: ${getClientDateFormat(period.dateFrom)} - ${getClientDateFormat(period.dateTo)}`}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [currencySymbol, goToUserContracts, openContractDetails, t, tab],
  );

  const highlightRow = (record: ContractItemPriceRaise): string => {
    const hasNoticeLeaveDate = Boolean(record.contract.noticeLeaveDate);

    return classNames({
      'bg-warnLight': hasNoticeLeaveDate && tab !== PriceRaiseTab.COMPLETED,
      'bg-successLight': tab === PriceRaiseTab.COMPLETED,
      'bg-errorLight': tab === PriceRaiseTab.UNFINISHED && !hasNoticeLeaveDate,
    });
  };

  const tableTitle = {
    [PriceRaiseTab.UNPROCESSED]: t('Unprocessed'),
    [PriceRaiseTab.PROCESSED]: t('Processed'),
    [PriceRaiseTab.COMPLETED]: t('Completed'),
    [PriceRaiseTab.UNFINISHED]: t('Unfinished'),
  }[tab];

  const statusFilter = {
    [PriceRaiseTab.COMPLETED]: ContractItemPriceRaiseStatus.COMPLETED,
    [PriceRaiseTab.UNFINISHED]: ContractItemPriceRaiseStatus.UNFINISHED,
  }[tab as PriceRaiseTab.COMPLETED | PriceRaiseTab.UNFINISHED];

  const listFilters = transformPriceRaiseListFilters(appliedFilters);

  return (
    <div className="space-y-4">
      <ServerTable
        fetchData={useGetContractItemPriceRaisesQuery}
        columns={columns}
        rowKey="contractItemPriceRaiseId"
        title={tableTitle}
        tableActions={tableActions}
        tableActionsPosition="prev"
        rowActions={rowActions}
        search
        defaultFilters={{
          ...listFilters,
          status: statusFilter || listFilters.status || processedStatuses,
          ...(tab === PriceRaiseTab.PROCESSED ? { isContractActive: true } : {}),
        }}
        searchPlaceholder={t('Enter customer or contract number')}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        rowClassName={highlightRow}
      />
      <SendPriceRaiseNotification
        isOpened={isSendNotificationOpened}
        isLoading={isSendNotificationLoading}
        onConfirm={sendNotification}
        onCancel={closeSendNotification}
      />
      <PriceRaiseListFilter
        showNextReviewDate
        showNotificationSentAt
        statusFilterMode={tab === PriceRaiseTab.PROCESSED ? 'processed' : undefined}
      />
    </div>
  );
});
